import { DefaultButton, Icon, PrimaryButton } from "@fluentui/react";
import { useEffect, useRef, useState } from "react";
import "./VideoFrame.scss";
import { ISentinel } from "../../Models/ISentinel";
import { AppConfig, getVideoRelayUrl, getVideoSdkUrlWithCamera } from "../../Config";

export interface IVideoFrameProps {
  sentinel: ISentinel;
  index: number;
  height: string;
  width: string;
  disableCamera?: boolean;
  // nome mostrato nei messaggi («La telecamera «Maria» non invia immagini»)
  cameraName?: string;
  // se c'e', nell'errore compare anche «Chiudi»
  onClose?: () => void;
}

export const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// dopo 8 s si avvisa che ci vuole piu' del solito, dopo 15 s si smette di aspettare
const SLOW_AFTER_MS = 8000;
const GIVE_UP_AFTER_MS = 15000;

type VideoPhase = "loading" | "slow" | "tap" | "live" | "error";

// iPhone in Risparmio energetico: il video della pagina relay non parte da solo, solo dopo un tocco. Il primo tocco
// ovunque nell'app (lo stesso che sblocca la sirena) lo avvia: la pagina relay e' sullo stesso sito, quindi qui si puo'
// chiamare play() sul suo <video> dentro il gesto (02/11/2026).
const startPausedVideos = () => {
  document.querySelectorAll<HTMLIFrameElement>("iframe.video-frame-iframe").forEach((f) => {
    try {
      const v = f.contentWindow?.document.getElementById("v") as HTMLVideoElement | null;
      if (v && v.paused) v.play().catch(() => {});
    } catch {
      // pagina video di un altro sito (player locale): non si tocca
    }
  });
};
if (typeof window !== "undefined") {
  ["touchend", "click"].forEach((ev) => document.addEventListener(ev, startPausedVideos, { capture: true, passive: true }));
}

// In cloud (relay) il browser conosce solo l'id della sentinella; in locale resta il player
// Dahua, che si collega direttamente alla telecamera con i suoi parametri (vuoto se incompleti).
const videoUrl = (sentinel: ISentinel): string => {
  if (AppConfig.videoSdk.relay) return getVideoRelayUrl(sentinel.id);
  if (!sentinel.cameraUrl || sentinel.cameraUrl.trim() === "") return "";
  return getVideoSdkUrlWithCamera(sentinel.cameraUrl.split(";").map((x) => x.trim()));
};

const VideoFrame = (props: IVideoFrameProps) => {
  const [phase, setPhase] = useState<VideoPhase>("loading");
  // ogni «Riprova» ricarica la pagina del video da capo
  const [attempt, setAttempt] = useState<number>(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const urlTarget = videoUrl(props.sentinel);

  useEffect(() => {
    if (props.disableCamera || urlTarget === "") return;
    setPhase("loading");

    // solo i messaggi della nostra pagina video: con piu' video aperti ognuno ascolta il suo
    let giveUp: ReturnType<typeof setTimeout> | undefined = undefined;
    const onMessage = (event: MessageEvent) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      if (event.data?.type === "IFRAME_READY") setPhase("live");
      if (event.data?.type === "VIDEO_ERROR") setPhase("error");
      // il video c'e' ma aspetta un tocco: non e' un guasto, si resta in attesa
      if (event.data?.type === "VIDEO_NEEDS_TAP") {
        clearTimeout(giveUp);
        // il messaggio sul Risparmio energetico solo sui telefoni (titolare): sul PC si resta su «Caricamento»
        if (window.matchMedia?.("(pointer: coarse)").matches) setPhase((p) => (p === "live" ? p : "tap"));
      }
    };
    window.addEventListener("message", onMessage);
    const slow = setTimeout(() => setPhase((p) => (p === "loading" ? "slow" : p)), SLOW_AFTER_MS);
    giveUp = setTimeout(() => setPhase((p) => (p === "live" ? p : "error")), GIVE_UP_AFTER_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(slow);
      clearTimeout(giveUp);
    };
  }, [attempt, urlTarget, props.disableCamera]);

  const name = props.cameraName?.trim() ?? "";
  const unconfigured = urlTarget === "";

  if (props.disableCamera) {
    return (
      <div className="video-frame" style={{ width: props.width, height: props.height }}>
        <div className="video-frame-overlay">
          <Icon iconName="VideoOff" className="video-frame-overlay-icon" />
          <div className="video-frame-overlay-title">Video spento</div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-frame" style={{ width: props.width, height: props.height }}>
      {!unconfigured && phase !== "error" && (
        <iframe
          key={attempt}
          ref={iframeRef}
          className="video-frame-iframe"
          src={urlTarget}
          title={name !== "" ? "Video: " + name : "Video"}
          allow="autoplay; fullscreen"
        ></iframe>
      )}

      {!unconfigured && (phase === "loading" || phase === "slow") && (
        <div className="video-frame-overlay" role="status">
          <div className="video-frame-spinner" aria-hidden="true"></div>
          <div className="video-frame-overlay-text">Caricamento live...</div>
          {phase === "slow" && (
            <div className="video-frame-overlay-hint">Sta impiegando più del solito…</div>
          )}
        </div>
      )}

      {/* il telefono ha bloccato l'avvio: testo e pulsante scelti dal titolare (02/11/2026); avvia anche un tocco ovunque */}
      {!unconfigured && phase === "tap" && (
        <div className="video-frame-overlay" role="status">
          <Icon iconName="Pause" className="video-frame-overlay-icon" />
          <div className="video-frame-overlay-title">Video in pausa: possibile Risparmio energetico.</div>
          <div className="video-frame-overlay-buttons">
            <PrimaryButton text="Avvia il video" iconProps={{ iconName: "Play" }} onClick={startPausedVideos} />
          </div>
        </div>
      )}

      {(unconfigured || phase === "error") && (
        <div className="video-frame-overlay" role="alert">
          <Icon iconName="VideoOff" className="video-frame-overlay-icon" />
          <div className="video-frame-overlay-title">
            {unconfigured ? "Telecamera non configurata" : "Video non disponibile"}
          </div>
          <div className="video-frame-overlay-hint">
            {unconfigured
              ? "Mancano i dati della telecamera: si inseriscono dalla configurazione del dispositivo."
              : "La telecamera non invia immagini."}
          </div>
          <div className="video-frame-overlay-buttons">
            {!unconfigured && (
              <PrimaryButton text="Riprova" iconProps={{ iconName: "Refresh" }} onClick={() => setAttempt((x) => x + 1)} />
            )}
            {props.onClose && <DefaultButton text="Chiudi" onClick={props.onClose} />}
          </div>
        </div>
      )}

      {phase === "live" && (
        <div className="video-frame-live">
          <span className="video-frame-live-dot" aria-hidden="true"></span>
          In diretta
        </div>
      )}
    </div>
  );
};

export default VideoFrame;
